'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import ControlledViewToggle, {
  IControlledViewToggleOption,
} from '@umodeapporg/ui/components/Form/ControlledViewToggle';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { formatCurrency, formatNumber } from '@/app/ops/helpers/formatters';
import { formatHoras, formatPercentual } from '@/app/relatorios/helpers/formatRelatorios';
import { CLASSES_SEM_COR, IClassesCorMarca } from '@/domain/cadastros/helpers/paletaMarcas';
import { cn } from '@/lib/utils';

import {
  IFatiaDaProducao,
  IOcupacaoPorMarca,
  pizzaDaProducao,
  TMedidaDaProducao,
  valorDaMedida,
} from '../helpers/ocupacaoPorMarca';
import { CARTEIRA_HREF, carteiraDaMarcaHref, carteiraDeVariasMarcasHref } from '../helpers/visaoExecutivaLinks';
import { BlocoCard } from './BlocoCard';
import { EstadoDoBloco } from './EstadoDoBloco';
import { IFatiaDesenhada, ProducaoPizza } from './ProducaoPizza';

/** A chave de medida, na ordem em que o bloco a oferece. */
const MEDIDAS: ReadonlyArray<TMedidaDaProducao> = ['HORAS', 'PECAS', 'FATURAMENTO'];

const LINHA = 'flex w-full items-center gap-2 rounded-md px-2 py-1 text-left';

interface ProducaoBlocoProps {
  leitura: IOcupacaoPorMarca;
  medida: TMedidaDaProducao;
  onMedida: (medida: TMedidaDaProducao) => void;
  /** O período escrito, para o subtítulo dizer de que janela são estes números. */
  periodoTexto: string;
  /**
   * A cor de cada marca, pela MESMA leitura que pinta as faixas do Mapa: ela vem da grade
   * do servidor, não do `corMapa` do cadastro, porque marca sem cor escolhida tem cor no
   * Mapa (a provisória da leitura) e ficaria cinza aqui.
   */
  coresDaMarca: (marca: string) => IClassesCorMarca;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * Quem ocupa a produção: a fatia de cada marca na carga programada do período.
 *
 * É outra pergunta que a performance por cliente: lá o assunto é dinheiro e risco, aqui é
 * quanto da fábrica cada marca está tomando. Por isso a medida é escolhida (horas, peças
 * ou faturamento) e a leitura sempre fecha com o total do período.
 */
export function ProducaoBloco({
  leitura,
  medida,
  onMedida,
  periodoTexto,
  coresDaMarca,
  loading,
  failed,
  onRetry,
  className,
}: ProducaoBlocoProps) {
  const { t } = useTranslation('common');
  const router = useRouter();

  const medidas: Array<IControlledViewToggleOption<TMedidaDaProducao>> = MEDIDAS.map((item) => ({
    value: item,
    label: t(`visaoExecutiva.producao.medida.${item}`),
  }));

  const pizza = pizzaDaProducao(leitura.fatias);
  const total = leitura.total;

  const nomeDaMarca = (marca: string): string => (marca === '' ? t('visaoExecutiva.producao.semMarca') : marca);

  const valorEscrito = (fatia: IFatiaDaProducao): string => {
    const valor = valorDaMedida(fatia, medida);
    if (medida === 'HORAS') {
      return formatHoras(valor);
    }
    return medida === 'PECAS' ? formatNumber(Math.round(valor)) : formatCurrency(valor);
  };

  const totalEscrito = (): string => {
    if (medida === 'HORAS') {
      return formatHoras(total);
    }
    return medida === 'PECAS' ? formatNumber(Math.round(total)) : formatCurrency(total);
  };

  const pctDe = (fatia: IFatiaDaProducao): number | null =>
    total > 0 ? (valorDaMedida(fatia, medida) / total) * 100 : null;

  const rotuloDaFatia = (fatia: IFatiaDaProducao, nome: string): string =>
    t('visaoExecutiva.producao.tooltip', {
      marca: nome,
      pct: formatPercentual(pctDe(fatia)),
      valor: valorEscrito(fatia),
      ops: formatNumber(fatia.ops),
    });

  /** A linha e a fatia abrem a MESMA Carteira: o recorte é o da marca clicada. */
  const abrirMarca = (fatia: IFatiaDaProducao, ehOutras: boolean): void => {
    const href = ehOutras ? carteiraDeVariasMarcasHref(pizza.marcasDeOutras) : carteiraDaMarcaHref(fatia.marca);
    router.push(href);
  };

  const linhas: Array<{ fatia: IFatiaDaProducao; nome: string; ehOutras: boolean }> = [
    ...pizza.maiores.map((fatia) => ({ fatia, nome: nomeDaMarca(fatia.marca), ehOutras: false })),
    ...(pizza.outras === null
      ? []
      : [
          {
            fatia: pizza.outras,
            nome: t('visaoExecutiva.producao.outras', { n: pizza.marcasDeOutras.length }),
            ehOutras: true,
          },
        ]),
  ];

  const desenhadas: Array<IFatiaDesenhada> = linhas.map(({ fatia, nome, ehOutras }) => ({
    chave: ehOutras ? 'outras' : `marca-${fatia.marca}`,
    valor: valorDaMedida(fatia, medida),
    // "Outras" é cinza de propósito: ela não é uma marca, é o resto.
    preenche: ehOutras ? CLASSES_SEM_COR.preenche : coresDaMarca(fatia.marca).preenche,
    rotulo: rotuloDaFatia(fatia, nome),
    onClick: () => abrirMarca(fatia, ehOutras),
  }));

  return (
    <BlocoCard
      pergunta={t('visaoExecutiva.producao.pergunta')}
      fonte={t('visaoExecutiva.producao.subtitulo', {
        medida: t(`visaoExecutiva.producao.medida.${medida}`),
        total: totalEscrito(),
        periodo: periodoTexto,
      })}
      acao={{ label: t('visaoExecutiva.producao.abrir'), href: CARTEIRA_HREF }}
      className={className}
    >
      <EstadoDoBloco
        loading={loading}
        failed={failed}
        vazio={leitura.fatias.length === 0}
        vazioTexto={t('visaoExecutiva.producao.vazio')}
        onRetry={onRetry}
      >
        <ControlledViewToggle<TMedidaDaProducao>
          options={medidas}
          value={medida}
          onChange={onMedida}
          size="sm"
          showLabels
          syncWithUrl={false}
          className="w-fit"
        />

        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
          {/* A pizza grande, ao lado da lista: ela é o desenho que responde "quem ocupa" de
              um olhar. Ainda ENCOLHE antes da lista (base flexível com piso), porque quem
              dá a resposta exata é o nome com o número ao lado. */}
          <div className="aspect-square w-40 min-w-32 shrink basis-40 laptop:w-48 laptop:basis-48">
            <ProducaoPizza
              fatias={desenhadas}
              rotulo={t('visaoExecutiva.producao.pizza', { total: totalEscrito() })}
              vazioTexto={t('visaoExecutiva.producao.pizzaVazia')}
            />
          </div>

          <ul className="flex min-w-[12rem] flex-1 flex-col gap-0.5">
            {linhas.map(({ fatia, nome, ehOutras }) => (
              <li key={ehOutras ? 'outras' : `marca-${fatia.marca}`} className="flex min-w-0">
                <ControlledTooltip content={rotuloDaFatia(fatia, nome)}>
                  <ControlledButton
                    defaultHtmlButton
                    type="button"
                    onClick={() => abrirMarca(fatia, ehOutras)}
                    className={cn(LINHA, 'interactive hover:bg-muted')}
                  >
                    {/* O quadradinho usa a MESMA cor da fatia: se os dois discordassem a
                      lista deixaria de explicar a pizza. */}
                    <span
                      className={cn(
                        'size-2.5 shrink-0 rounded-sm',
                        ehOutras ? CLASSES_SEM_COR.fundo : coresDaMarca(fatia.marca).fundo,
                      )}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold text-foreground">{nome}</span>
                    <span className="shrink-0 text-xs tabular-nums text-foreground-muted">
                      {formatPercentual(pctDe(fatia))}
                    </span>
                    <span className="w-20 shrink-0 text-right text-xs tabular-nums text-foreground-subtle">
                      {valorEscrito(fatia)}
                    </span>
                  </ControlledButton>
                </ControlledTooltip>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-auto text-xs text-foreground-subtle">
          {t('visaoExecutiva.producao.opsAtivas', { count: leitura.ops })}
        </p>
      </EstadoDoBloco>
    </BlocoCard>
  );
}
